import { Injectable } from "@angular/core";
import { DateTime } from "luxon";
import { Constant } from "../constant/constant";
import { formatDaysAndHoursLong } from "../../../../utils/format-days-hours";

@Injectable({
  providedIn: "root",
})
export class CommonService {
  public emailValidationPattern: RegExp;

  constructor(private constant: Constant) {
    this.emailValidationPattern = this.constant.EMAIL_VALIDATION_PATTERN;
  }

  getVacationName(id, timeoffTypesList): string {
    let vacationName = "";
    timeoffTypesList.forEach((element) => {
      if (element.id == id) {
        vacationName = (element || {}).label || "";
      }
    });
    return vacationName;
  }

  getVacationLabelColor(id, timeoffTypesList): string {
    let vacationLabelColor = "";
    timeoffTypesList.forEach((element) => {
      if (element.id == id) {
        vacationLabelColor = (element || {}).label_color || "";
      }
    });
    return vacationLabelColor;
  }

  // BP-EVTA-27-04-04 — anchor to UTC so backend-emitted UTC-midnight ISO
  // strings (e.g., requestDateFrom = "2026-01-01T00:00:00.000Z") render as
  // their UTC calendar date regardless of viewer timezone. Without { zone:
  // "utc" }, a viewer west of UTC sees a UTC-midnight date render as the
  // previous local day. Use for ALL backend-emitted date strings; do NOT
  // use for user-typed local-zone Date objects.
  getDate(date) {
    if (!!date) {
      return DateTime.fromISO(date, { zone: "utc" }).toFormat(" dd LLL yyyy");
    }
  }

  // BP-EVTA-27-04-05 — canonical "X Day(s) and Y hours" label for vacation
  // grid rows. Reads details.totalHours (engine-emitted number on every
  // synthesised or submitted row) and decomposes via formatDaysAndHoursLong.
  // Falls back to vacation.requestDays for rows that lack details.totalHours.
  //
  // Consumers pass orgHours from their own context (this.orgHours or
  // this.organizationWorkingHours?.hoursinworkday). The helper does not
  // assume a default; callers are responsible.
  getDaysUsedLabel(vacation: any, orgHours: number): string {
    const totalHours = Number(vacation?.details?.totalHours);
    if (!Number.isFinite(totalHours)) {
      return vacation?.requestDays ?? "";
    }
    return formatDaysAndHoursLong(totalHours, orgHours);
  }

  getManagerName(id, employeesList): string {
    let managerName = "";
    employeesList.forEach((element) => {
      if (element.id == id) {
        managerName =
          element.name.last +
          ", " +
          element.name.first +
          " " +
          element.name?.middle;
      }
    });
    return managerName;
  }

  getEmployeeName(id, employeesList): string {
    let empName = "";
    employeesList.forEach((element) => {
      if (element.id == id) {
        empName =
          element.name.last +
          ", " +
          element.name.first +
          " " +
          element.name?.middle;
      }
    });
    return empName;
  }

  validateEmail(email: string): boolean {
    return this.emailValidationPattern.test(email);
  }

  formatToLocalTimeFromUTC = (utcDateString: string): Date => {
    const utcDateTime: Date = new Date(utcDateString);
    const localDateTime: Date = new Date(
      utcDateTime.getTime() + utcDateTime.getTimezoneOffset() * 60 * 1000
    );
    return localDateTime;
  };

  utcToJSStr = (isoDateStr: string): Date | string => {
    if (isoDateStr) {
      const dateObj = DateTime.fromISO(isoDateStr, { zone: "utc" });
      const jsDate = new Date(dateObj.year, dateObj.month - 1, dateObj.day);
      jsDate.setHours(0, 0, 0, 0);
      return DateTime.fromJSDate(jsDate).toFormat("MMM d, yyyy");
    }
    return isoDateStr;
  };

  utcToJSStartOfDay = (isoDateStr: string): Date | string => {
    if (isoDateStr) {
      const dateObj = DateTime.fromISO(isoDateStr, { zone: "utc" });
      const jsDate = new Date(dateObj.year, dateObj.month - 1, dateObj.day);
      jsDate.setHours(0, 0, 0, 0);
      return jsDate;
    }
    return isoDateStr;
  };

  jsDateToUTCStartOfDay = (jsDate: Date): string => {
    if (!jsDate) return "";

    if (!(jsDate instanceof Date)) return jsDate;

    return DateTime.utc(
      jsDate.getFullYear(),
      jsDate.getMonth() + 1,
      jsDate.getDate()
    )
      .startOf("day")
      .toISO();
  };

  jsDateToISODateInUTC = (
    jsDate: Date,
    hour: number,
    minute: number
  ): string => {
    if (!jsDate) return "";

    if (!(jsDate instanceof Date)) return jsDate;

    const utcDate = new Date(
      Date.UTC(
        jsDate.getFullYear(),
        jsDate.getMonth(),
        jsDate.getDate(),
        hour,
        minute
      )
    );
    const utcDateString = utcDate.toISOString(); // UTC date string
    return utcDateString;
  };

}
